import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, DataTable, Loader, Tag } from "@threadws/jute";
import type { Connection } from "../../../api/generated";
import {
  googleOAuthStartMutation,
  personaConnectionsListOptions,
} from "../../../api/generated/@tanstack/react-query.gen";
import { UnlinkConnectionDialog } from "./UnlinkConnectionDialog";

const columns = [
  { key: "account", label: "Account" },
  { key: "status", label: "Status" },
  { key: "actions", label: "", align: "right" as const },
];

const KIND_LABELS: Record<Connection["kind"], string> = { google: "Google", mcp: "MCP server" };

/** The accounts a persona uses, with buttons to connect a Google account or unlink one. */
export function PersonaConnections({ personaId }: { personaId: string }) {
  const { data: connections, isPending, error } = useQuery(personaConnectionsListOptions({ path: { id: personaId } }));
  const [toUnlink, setToUnlink] = useState<Connection | null>(null);
  // Google's consent page takes over the tab, so stay "pending" until the browser leaves.
  const start = useMutation({
    ...googleOAuthStartMutation(),
    onSuccess: ({ url }) => window.location.assign(url),
  });
  const connecting = start.isPending || start.isSuccess;
  const connectGoogle = () => start.mutate({ body: { personaId } });

  const rows = (connections ?? []).map((connection) => ({
    id: connection.id,
    account: (
      <span className="flex flex-col">
        <span>{connection.label}</span>
        <span className="text-sm text-text-secondary">{KIND_LABELS[connection.kind]}</span>
      </span>
    ),
    status:
      connection.status === "active" ? <Tag tone="success">Connected</Tag> : <Tag tone="warning">Needs reconnecting</Tag>,
    actions: (
      <span className="flex justify-end gap-2">
        {connection.status === "needs_reauth" && connection.kind === "google" && (
          <Button variant="secondary" size="sm" icon="" disabled={connecting} onClick={connectGoogle}>
            Reconnect
          </Button>
        )}
        <Button variant="ghost" size="sm" icon="" onClick={() => setToUnlink(connection)}>
          Unlink
        </Button>
      </span>
    ),
  }));

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium">Connections</h2>
          <p className="text-sm text-text-secondary">Accounts this persona can use, such as a Google account for its calendars.</p>
        </div>
        <Button variant="secondary" disabled={connecting} onClick={connectGoogle}>
          {connecting ? "Opening Google…" : "Connect Google account"}
        </Button>
      </header>
      {start.error && <p className="text-danger">{start.error.message}</p>}
      {isPending ? (
        <Loader label="Loading connections" />
      ) : error ? (
        <p className="text-danger">Couldn't load connections: {error.message}</p>
      ) : rows.length === 0 ? (
        <p className="text-text-secondary">No accounts connected yet.</p>
      ) : (
        <DataTable columns={columns} rows={rows} />
      )}
      <UnlinkConnectionDialog personaId={personaId} connection={toUnlink} onClose={() => setToUnlink(null)} />
    </section>
  );
}
